import { Link } from "react-router-dom";
import { ArrowRight, CheckCircle2 } from "lucide-react";
import { motion } from "framer-motion";
import CTASection from "@/components/CTASection";
import SEO from "@/components/SEO";

const IMG =
  "https://images.unsplash.com/photo-1638356554489-decb2e3a4c37?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2Njl8MHwxfHNlYXJjaHwxfHxhcnRpZmljaWFsJTIwaW50ZWxsaWdlbmNlJTIwZ2xvd2luZyUyMG5vZGVzJTIwZGFya3xlbnwwfHx8fDE3Nzc1MjM2NDl8MA&ixlib=rb-4.1.0&q=85";

const FEATURES = [
  {
    title: "Universal AI Observability",
    desc: "Capture every LLM call, prompt, response, tool-use and cost across every team and vendor: OpenAI, Anthropic, Google, internal models.",
  },
  {
    title: "Workflow Provenance",
    desc: "Trace any AI artifact back through every step that produced it: prompts, retrievals, models, humans, decisions.",
  },
  {
    title: "Policy & Guardrails",
    desc: "Enforce enterprise policy at the edge: redact PII, block restricted domains, require approvals, route sensitive tasks.",
  },
  {
    title: "Spend & Risk Analytics",
    desc: "Unified cost, usage and risk dashboards across teams, projects and vendors. Spot runaways in real time.",
  },
  {
    title: "Compliance-Ready",
    desc: "Audit trails, access controls, retention, and export. SOC2, ISO 27001, GDPR alignment built into the data model.",
  },
  {
    title: "Bring-Your-Own-Cloud",
    desc: "Deploy entirely inside your VPC or on-prem. Your prompts, responses and metadata never leave your perimeter.",
  },
];

const USE_CASES = [
  "CTOs establishing AI governance across business units",
  "Security & risk teams auditing model use",
  "Finance teams tracking AI spend accurately",
  "Platform teams enforcing AI policy at scale",
];

export default function ProductAIAnalytics() {
  return (
    <>
      <SEO
        title="Enterprise AI Usage Analytics & Workflow Provenance"
        description="Enterprise-grade observability for generative AI: unified analytics, workflow provenance, policy guardrails, and spend control, deployable on your own cloud."
        path="/products/ai-usage-analytics"
        image={IMG}
      />
      <section className="relative overflow-hidden border-b border-white/10 bg-obsidian">
        <div
          className="absolute inset-0 opacity-[0.18]"
          style={{
            backgroundImage: `url(${IMG})`,
            backgroundSize: "cover",
            backgroundPosition: "center",
          }}
        />
        <div className="absolute inset-0 bg-gradient-to-b from-obsidian/70 via-obsidian/90 to-obsidian" />
        <div className="absolute inset-0 bg-grid opacity-50" />

        <div className="relative max-w-7xl mx-auto px-5 lg:px-8 py-24 lg:py-32">
          <div className="chip text-neon-cyan">&gt; product.02 // ai_usage_analytics</div>
          <motion.h1
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="mt-4 font-display font-black text-4xl sm:text-5xl lg:text-7xl text-white tracking-tighter leading-[0.95] max-w-5xl"
          >
            Enterprise AI Usage Analytics &{" "}
            <span className="text-glow-cyan text-neon-cyan">Workflow Provenance</span>
          </motion.h1>
          <p className="mt-6 max-w-2xl text-white/70 text-lg leading-relaxed">
            Know exactly how AI is used across your organization: which teams,
            which workflows, which models, at what cost, and with what risk. Full
            provenance. Enterprise-grade. Deployable on your own cloud.
          </p>
          <div className="mt-10 flex flex-wrap gap-4">
            <Link
              to="/contact"
              data-testid="ai-product-cta"
              className="group inline-flex items-center gap-2 bg-neon-cyan text-obsidian px-6 py-3.5 text-sm font-mono font-bold uppercase tracking-widest hover:shadow-[0_0_40px_rgba(0,255,255,0.6)] transition-all"
            >
              Request a Demo <ArrowRight size={16} className="group-hover:translate-x-1 transition-transform" />
            </Link>
            <a
              href="mailto:contact@unitedtechlab.com"
              className="inline-flex items-center gap-2 border border-white/20 text-white px-6 py-3.5 text-sm font-mono font-semibold uppercase tracking-widest hover:border-neon-cyan hover:text-neon-cyan transition-all"
            >
              Talk to engineering
            </a>
          </div>
        </div>
      </section>

      <section className="border-b border-white/10 bg-carbon py-24">
        <div className="max-w-7xl mx-auto px-5 lg:px-8">
          <div className="chip text-neon-cyan">&gt; /capabilities</div>
          <h2 className="mt-3 font-display font-black text-4xl md:text-5xl text-white leading-tight max-w-3xl">
            Observability & governance for the AI era.
          </h2>

          <div className="mt-12 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-px bg-white/10 border border-white/10">
            {FEATURES.map((f, i) => (
              <div
                key={f.title}
                className="bg-obsidian p-8 hover:bg-[#0f0f0f] transition-colors"
                data-testid={`ai-feature-${i}`}
              >
                <div className="chip text-neon-cyan">[F.0{i + 1}]</div>
                <h3 className="mt-4 font-display font-bold text-white text-xl">{f.title}</h3>
                <p className="mt-2 text-sm text-white/60 leading-relaxed">{f.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="border-b border-white/10 bg-obsidian py-24">
        <div className="max-w-7xl mx-auto px-5 lg:px-8 grid grid-cols-1 lg:grid-cols-12 gap-10">
          <div className="lg:col-span-5">
            <div className="chip text-neon-cyan">&gt; /use_cases</div>
            <h3 className="mt-3 font-display font-black text-3xl md:text-4xl text-white leading-tight">
              Built for enterprise AI teams.
            </h3>
          </div>
          <ul className="lg:col-span-7 grid grid-cols-1 sm:grid-cols-2 gap-3">
            {USE_CASES.map((u) => (
              <li
                key={u}
                className="flex items-start gap-3 border border-white/10 p-5 bg-carbon"
              >
                <CheckCircle2 size={18} className="text-neon-cyan mt-0.5 shrink-0" />
                <span className="text-sm text-white/80 leading-relaxed">{u}</span>
              </li>
            ))}
          </ul>
        </div>
      </section>

      <CTASection
        eyebrow="> product.ai_usage_analytics"
        title="Get visibility into your AI before your auditors ask."
        subtitle="Tell us your stack. We'll show you exactly what you'd see after a 30-minute install."
      />
    </>
  );
}
